/**
 * Decoder backup: the guest's decoder holding frames it has received but not emitted.
 *
 * This is the latency a player feels that no network meter can see. The wire already
 * carries the raw ingredients (standing depth, decode fps, the growth trend, the episode);
 * this module turns them into ONE named state so a replay, the stream-health record and a
 * HUD all say the same thing about it: whether the decoder is backed up, how deep, how many
 * milliseconds that is at the current decode rate, and WHY — a decoder that cannot sustain
 * the send rate (throughput deficit: fewer frames per second helps) versus one that keeps
 * pace but is sitting on a queue (standing depth: fewer frames per second makes it WORSE).
 *
 * Nothing here acts. The decision what to do about a backup lives in the recovery manager
 * and the tier selection; this is the eyes, deliberately kept apart from the hands so the
 * classification can be trusted in a log even when the response is being changed.
 *
 * Two depth sources, so no engine is blind (see `DecoderBackupDepthSource`): the frame
 * counters where the engine has them, and the decoder's own per-frame residency where it
 * does not (Safari). Both are reported in frames AND ms, and the source is always named.
 */

import { decoderDutyCycle, isDecodeThroughputDeficit } from './decodeLagContracts';

/**
 * Standing depth at or above which the decoder is called backed up. A hardware decoder
 * legitimately keeps a couple of frames in flight; three or more sitting undecoded is a
 * queue. UNVALIDATED ESTIMATE — the `[DecoderBackup]` debug line exists to calibrate it.
 */
export const DECODER_BACKUP_MIN_STANDING_FRAMES = 3;

/**
 * How long a backup must hold before it is reported as begun. Matches the decode-lag dwell:
 * a single-tick spike that clears on the next sample is a stall, not a backup.
 */
export const DECODER_BACKUP_DWELL_MS = 2_000;

export type DecoderBackupClassification =
  /** Depth below the threshold and no active episode: the decoder is keeping up. */
  | 'none'
  /**
   * Decode rate well under the rate DELIVERED, and (where residency says so) a decoder with
   * no idle time left: it cannot sustain what it is sent. Fewer frames per second helps.
   */
  | 'throughput-deficit'
  /**
   * Frames are queued but the decoder is keeping pace with what arrives, or has idle time to
   * spare. Depth, not capacity — fewer frames per second makes the wait LONGER.
   */
  | 'standing-depth';

/**
 * Where the depth figure came from.
 *
 * - `counters`: framesReceived − framesDecoded − framesDropped (Chromium, Android). Counts
 *   every frame delivered but not yet emitted, including those still queued for decode.
 * - `decode-residency`: `decodeFps × decodeMsPerFrame` (Little's law) — frames resident
 *   inside the decoder, from the decoder's own timing. The only depth Safari can report:
 *   it exposes no framesReceived, but it does expose totalDecodeTime. Excludes any queue
 *   ahead of the decoder, so it is a floor on the true depth, never an overstatement.
 * - `none`: neither measured this tick.
 */
export type DecoderBackupDepthSource = 'counters' | 'decode-residency' | 'none';

/** One tick's raw ingredients, straight off the clientStats wire plus the host's target. */
export type DecoderBackupReading = {
  /** framesReceived − framesDecoded − framesDropped, absolute. Undefined when unmeasured. */
  standingBacklogFrames?: number;
  /** Wall time a frame spent inside the decoder this interval, ms. Undefined when unmeasured. */
  decodeMsPerFrame?: number;
  /** Guest inbound-rtp framesPerSecond. */
  decodeFps?: number;
  /**
   * Frames per second the host actually PUT ON THE WIRE for this peer (measured encode
   * rate), not the rate it intended to. A guest cannot decode frames that were never sent,
   * so judging its decode rate against an intended target reads the host's own cuts back as
   * the guest's deficit.
   */
  deliveredFps: number;
  /** Growth of decode-side playout delay above its window minimum, ms. */
  playoutLatencyTrendMs?: number;
  /** True while the guest reports an active decode-lag episode. */
  episodeActive: boolean;
};

/** The classified state for one tick. */
export type DecoderBackupObservation = {
  classification: DecoderBackupClassification;
  /** The depth judged, in frames — from `depthSource`. */
  standingBacklogFrames?: number;
  /** The depth as wait time at the current decode rate, ms. */
  standingBacklogMs?: number;
  depthSource: DecoderBackupDepthSource;
  /** Decoder residency per frame this tick, ms, whenever the engine reported it. */
  decodeMsPerFrame?: number;
  decodeFps?: number;
  deliveredFps: number;
  /** How far the decode rate is under the delivered rate, percent (0 when at or above). */
  decodeDeficitPct: number;
  /**
   * Fraction of wall time the decoder spent decoding, percent. The figure that separates a
   * decoder that cannot keep up from one that was not given much to do; absent when the
   * engine reports no residency.
   */
  decodeDutyCyclePct?: number;
  playoutLatencyTrendMs?: number;
  episodeActive: boolean;
};

function finite(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Classify one tick. Pure. */
export function classifyDecoderBackup(reading: DecoderBackupReading): DecoderBackupObservation {
  const { standingBacklogFrames, decodeMsPerFrame, decodeFps, deliveredFps, playoutLatencyTrendMs, episodeActive } =
    reading;
  const fpsMeasured = finite(decodeFps);
  const drainRateKnown = fpsMeasured && decodeFps > 0;
  const deliveredValid = Number.isFinite(deliveredFps) && deliveredFps > 0;
  const decodeDeficitPct =
    fpsMeasured && deliveredValid ? Math.max(0, ((deliveredFps - decodeFps) / deliveredFps) * 100) : 0;
  const dutyCycle = fpsMeasured ? decoderDutyCycle(decodeFps, decodeMsPerFrame) : null;
  const deficit = isDecodeThroughputDeficit({
    ...(fpsMeasured ? { decodeFps } : {}),
    deliveredFps,
    ...(finite(decodeMsPerFrame) ? { decodeMsPerFrame } : {}),
  });

  // Depth: the counters when the engine has them; otherwise the decoder's own residency.
  // A decoder reporting 0fps has no drain rate — frames are known, the ms are not, and the
  // residency estimate (which needs the rate) is not available at all.
  let depthFrames: number | undefined;
  let depthMs: number | undefined;
  let depthSource: DecoderBackupDepthSource = 'none';
  if (finite(standingBacklogFrames)) {
    depthSource = 'counters';
    depthFrames = standingBacklogFrames;
    depthMs = drainRateKnown ? Math.round((standingBacklogFrames / decodeFps) * 1_000) : undefined;
  } else if (finite(decodeMsPerFrame) && drainRateKnown) {
    depthSource = 'decode-residency';
    depthFrames = Math.round((decodeMsPerFrame / 1_000) * decodeFps);
    depthMs = Math.round(decodeMsPerFrame);
  }

  const backedUp = (depthFrames !== undefined && depthFrames >= DECODER_BACKUP_MIN_STANDING_FRAMES) || episodeActive;
  let classification: DecoderBackupClassification = 'none';
  if (backedUp) classification = deficit ? 'throughput-deficit' : 'standing-depth';

  return {
    classification,
    ...(depthFrames !== undefined ? { standingBacklogFrames: depthFrames } : {}),
    ...(depthMs !== undefined ? { standingBacklogMs: depthMs } : {}),
    depthSource,
    ...(finite(decodeMsPerFrame) ? { decodeMsPerFrame: Math.round(decodeMsPerFrame * 10) / 10 } : {}),
    ...(fpsMeasured ? { decodeFps } : {}),
    deliveredFps,
    decodeDeficitPct: Math.round(decodeDeficitPct),
    ...(dutyCycle !== null ? { decodeDutyCyclePct: Math.round(dutyCycle * 100) } : {}),
    ...(playoutLatencyTrendMs !== undefined ? { playoutLatencyTrendMs } : {}),
    episodeActive,
  };
}

/** What changed this tick, for the one-per-edge health events. */
export type DecoderBackupTransition =
  /** Backed up past the dwell for the first time in this backup. */
  | 'began'
  /** Was reported begun; now clear. */
  | 'ended'
  /** Reported begun and still backed up. */
  | 'continuing'
  /** Backed up but the dwell has not elapsed yet. */
  | 'holding'
  /** Not backed up, and nothing was begun. */
  | 'clear';

type BackupState = {
  sinceMs: number | null;
  begun: boolean;
  peakFrames: number;
  peakMs: number;
};

/**
 * Per-key edge detector over `classifyDecoderBackup`: says when a backup BEGINS (held past
 * the dwell) and when it ENDS, with the peak depth seen, so the health record carries two
 * events per backup instead of one per tick.
 */
export class DecoderBackupTracker {
  private readonly stateByKey = new Map<string, BackupState>();

  constructor(private readonly options: { dwellMs?: number; nowMs?: () => number } = {}) {}

  private nowMs(): number {
    return this.options.nowMs?.() ?? Date.now();
  }

  private dwellMs(): number {
    return this.options.dwellMs ?? DECODER_BACKUP_DWELL_MS;
  }

  /** Observe one tick for a key. */
  public observe(
    key: string,
    reading: DecoderBackupReading,
  ): {
    observation: DecoderBackupObservation;
    transition: DecoderBackupTransition;
    /** Present on 'ended': how long the backup was reported and how deep it got. */
    ended?: { durationMs: number; peakFrames: number; peakMs: number };
  } {
    const observation = classifyDecoderBackup(reading);
    const now = this.nowMs();
    const state = this.stateByKey.get(key) ?? { sinceMs: null, begun: false, peakFrames: 0, peakMs: 0 };

    if (observation.classification === 'none') {
      const wasBegun = state.begun;
      const ended =
        wasBegun && state.sinceMs !== null
          ? { durationMs: now - state.sinceMs, peakFrames: state.peakFrames, peakMs: state.peakMs }
          : undefined;
      this.stateByKey.delete(key);
      return { observation, transition: wasBegun ? 'ended' : 'clear', ...(ended ? { ended } : {}) };
    }

    if (state.sinceMs === null) state.sinceMs = now;
    state.peakFrames = Math.max(state.peakFrames, observation.standingBacklogFrames ?? 0);
    state.peakMs = Math.max(state.peakMs, observation.standingBacklogMs ?? 0);

    let transition: DecoderBackupTransition;
    if (state.begun) {
      transition = 'continuing';
    } else if (now - state.sinceMs >= this.dwellMs()) {
      state.begun = true;
      transition = 'began';
    } else {
      transition = 'holding';
    }
    this.stateByKey.set(key, state);
    return { observation, transition };
  }

  public clearKey(key: string): void {
    this.stateByKey.delete(key);
  }

  public clearAll(): void {
    this.stateByKey.clear();
  }
}
